import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { REGION_PHONE_CONFIG, INTL_TEL_INPUT_VALIDATION_NUMBER_TYPES } from '@constants/app.constants';
import { environment } from '@environments/environment';
import { HelperService } from '@services/helper.service';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import * as intlTelInputUtils from 'intl-tel-input/build/js/utils.js';

declare let intlTelInput: any;

@Component({
  selector: 'app-calio-tel-input',
  templateUrl: './calio-tel-input.component.html',
  styleUrls: ['./calio-tel-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalioTelInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CalioTelInputComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgClass, ReactiveFormsModule]
})

export class CalioTelInputComponent implements OnInit, OnChanges, ControlValueAccessor, Validator, AfterViewInit {

  private helperService = inject(HelperService);

  @Input() defaultCountry: string;
  @Input() cssClass: {};
  @Input() E164PhoneNumber: string;
  @Input() label: string;
  @Input() labelCssClass: string;
  @Input() name = 'intl-tel-input-name';
  @Input() onlyLocalized = true;
  @Input() options: any = {};
  // Lowercase ISO-2 codes to restrict the country dropdown to (e.g. ['ch','de']).
  // Empty = offer every country (the default). Read once at init.
  @Input() onlyCountries: string[] = [];
  @Input() required = false;
  @Input() isDisabled = false;

  @Output() blurEvent = new EventEmitter<any>();

  @ViewChild('intlTelInput') private _inputElement: ElementRef;

  private _intlTelInput: any;
  private onTouch: Function;
  private onModelChange: Function;
  private onValidatorChange: Function;
  // Region-driven phone config (fallback country + preferred ordering); 'eu' if region is unset/unknown.
  private readonly phoneConfig = REGION_PHONE_CONFIG[environment.region] ?? REGION_PHONE_CONFIG['eu'];
  readonly preferredCountries = this.phoneConfig.preferredCountries;

  telInputControl: FormControl = new FormControl();

  modifyCountryData(): void {
    (window as any)?.intlTelInput?.getCountryData().forEach((country: any) =>
      country.name = country.name.replace(/.+\((.+)\)/, '$1'));
  }

  ngOnInit(): void {
    this.telInputControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((inputValue: string) => {
        if (!inputValue) {
          if (this.onModelChange) {
            this.onModelChange(inputValue);
          }
        }
        if (inputValue && this.onModelChange) {
          this.setPhoneNumber(inputValue);
          if (this.onTouch && !this.telInputControl.touched) {
            this.onTouch();
          }
          if (this.onValidatorChange) {
            this.onValidatorChange();
          }
        }
      });

    if (this.isDisabled) {
      this.telInputControl.disable();
    }
  }

  onClick(): void {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  onBlur(): void {
    if (this.onTouch) {
      this.onTouch();
    }
    this.blurEvent.emit();
  }

  ngAfterViewInit(): void {
    this.buildTelInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // `onlyCountries` can resolve AFTER ngAfterViewInit: the signup-policy load is
    // async and, in PLG mode, the phone field mounts before it returns. intl-tel-input
    // options are immutable once built, so rebuild the widget when the restriction
    // arrives late -- otherwise the country dropdown silently offers every country
    // for the rest of the session.
    if (changes['onlyCountries'] && !changes['onlyCountries'].firstChange && this._intlTelInput) {
      const currentNumber = this.E164PhoneNumber || this._intlTelInput.getNumber();
      this._intlTelInput.destroy();
      this.buildTelInput();
      if (currentNumber) {
        this._intlTelInput.setNumber(currentNumber);
      }
    }
  }

  private buildTelInput(): void {
    this.options = {
      initialCountry: 'auto',
      separateDialCode: true,
      preferredCountries: this.preferredCountries,
      // Restrict the dropdown to the admin's allowed countries when the signup
      // policy configures `allowed_country_codes`; otherwise offer every country.
      ...(this.onlyCountries?.length ? { onlyCountries: this.onlyCountries.map(code => code.toLowerCase()) } : {}),
      formatOnDisplay: true,
      autoHideDialCode: true,
      loadUtils: () => Promise.resolve({ ...intlTelInputUtils }),
      validationNumberTypes: INTL_TEL_INPUT_VALIDATION_NUMBER_TYPES,
      countrySearch: true,
      geoIpLookup: callback => {
        firstValueFrom(this.helperService.getUserCountry())
          .then(data => callback(data?.country || this.phoneConfig.defaultCountry))
          .catch(() => callback(this.phoneConfig.defaultCountry));
      },
    };

    const intlTelInputInstance = intlTelInput;
    this._intlTelInput = intlTelInputInstance(this._inputElement.nativeElement, this.options);
  }

  setPhoneNumber(value: string): void {
    if (this._intlTelInput) {
      if (!!value) {
        this._intlTelInput.setNumber(value);
      }
      this.i18nizePhoneNumber();
    } else {
    }
  }

  i18nizePhoneNumber(): void {
    if (Number(this._intlTelInput.telInput.style.paddingLeft.replace('px', '')) > 100) {
      const widthOfCountryDialCode = this._intlTelInput.selectedDialCode.parentElement.clientWidth;
      this._intlTelInput.telInput.style.paddingLeft = `${(widthOfCountryDialCode + 3.97)}px`;
    }
    this.E164PhoneNumber = this._intlTelInput.getNumber();
    if (this.E164PhoneNumber) {
      this.onModelChange(this.E164PhoneNumber);
    } else {
      if (this._intlTelInput.selectedDialCode && this._intlTelInput.selectedDialCode.innerText) {
        if (this.telInputControl.value && this.telInputControl.value[0] === '+') {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + this._intlTelInput.telInput.value;
        } else {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + (this.telInputControl.value ? this.telInputControl.value : '');
        }
        this.onModelChange(this.E164PhoneNumber);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this.telInputControl.setValue(obj);
  }

  validate(control: AbstractControl): ValidationErrors {

    if (control.value && this._intlTelInput) {
      const selectedCountryCode = this._intlTelInput.getSelectedCountryData();
      if (control.value === `+${selectedCountryCode.dialCode}`) {
        if (this.required) {
          control.setErrors({
            required: true
          });
          return control.errors;
        }
      } else if (!this._intlTelInput.isValidNumber() && this._intlTelInput.isValidNumber() !== null) {
        control.setErrors({
          invalidNumber: true
        });
        return control.errors;
      }
    }
    return null;
  }

  registerOnValidatorChange?(fn: any): void {
    this.onValidatorChange = fn;
  }
}
